import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, LockKeyhole, Mail, ShieldCheck } from "lucide-react";
import { Toaster, toast } from "sonner";
import { authApi, formatApiError } from "@/lib/adminApi";

const SESSION_CONFLICT_MESSAGE =
  "User is already logged in on another window/device.";

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [forceLoading, setForceLoading] = useState(false);

  const from = location.state?.from || "/";

  const goAfterLogin = () => {
    navigate(from, { replace: true });
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!email.trim() || !password) {
      toast.error("Please enter email and password.");
      return;
    }

    setLoading(true);

    try {
      await authApi.login(email.trim(), password);
      goAfterLogin();
    } catch (error) {
      if (error?.response?.status === 409 || error?.isSessionConflict) {
        setConflictOpen(true);
        return;
      }

      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleForceLogin = async () => {
    setForceLoading(true);

    try {
      await authApi.forceLogin(email.trim(), password);
      setConflictOpen(false);
      goAfterLogin();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setForceLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Toaster position="top-center" richColors />

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-[780px]">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-14 h-14 rounded-2xl overflow-hidden border border-border bg-card shadow-sm flex items-center justify-center">
              <img
                src="/logo192.png"
                alt="Prathvi Power Solutions"
                className="w-full h-full object-cover"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
              <ShieldCheck className="w-7 h-7 text-foreground" />
            </div>
            <div className="font-heading text-xl sm:text-2xl font-bold">
              Prathvi Power Solutions
            </div>
          </div>

          <section className="mx-auto w-full max-w-[480px] rounded-2xl border border-border bg-card shadow-xl p-7 sm:p-9">
            <div className="mb-7">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground mb-2">
                <ShieldCheck className="w-3.5 h-3.5" />
                Secure Sign In
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black leading-tight">
                Sign in to Prathvi Power Solutions
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Sign in with your assigned account to open the main website.
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label
                  htmlFor="login-email"
                  className="block mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
                >
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    id="login-email"
                    data-testid="login-email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="Enter your email"
                    className="w-full h-12 rounded-xl border border-border bg-background pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-foreground/20"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="login-password"
                  className="block mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
                >
                  Password
                </label>
                <div className="relative">
                  <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    id="login-password"
                    data-testid="login-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Enter your password"
                    className="w-full h-12 rounded-xl border border-border bg-background pl-10 pr-11 text-sm outline-none focus:ring-2 focus:ring-foreground/20"
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl bg-foreground text-background font-bold text-sm disabled:opacity-60"
              >
                {loading ? "Signing In..." : "Sign In"}
              </button>
            </form>

            <div className="mt-6 flex items-center justify-between text-xs">
              <Link
                to="/admin/forgot-password"
                className="text-muted-foreground hover:text-foreground hover:underline"
              >
                Forgot password?
              </Link>
              <span className="text-muted-foreground">Secure session</span>
            </div>
          </section>
        </div>
      </main>

      <footer className="pb-8 text-center text-xs text-muted-foreground">
        © 2026 Prathvi Power Solutions
      </footer>

      {conflictOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="session-conflict-title"
            className="w-full max-w-md rounded-2xl border border-border bg-card shadow-2xl p-6"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2
                  id="session-conflict-title"
                  className="font-heading text-lg font-bold"
                >
                  Active Session Found
                </h2>
                <p className="mt-2 text-sm font-medium">
                  {SESSION_CONFLICT_MESSAGE}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Do you want to logout the existing session and continue
                  logging in here?
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                disabled={forceLoading}
                onClick={() => setConflictOpen(false)}
                className="h-10 px-4 rounded-lg border border-border text-sm font-semibold hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={forceLoading}
                onClick={handleForceLogin}
                className="h-10 px-4 rounded-lg bg-foreground text-background text-sm font-semibold disabled:opacity-50"
              >
                {forceLoading
                  ? "Logging Out Existing Session..."
                  : "Logout Existing Session & Login"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
